// @flow
import * as React from 'react';

import { Label, PrimaryText, Error } from '../../../Typography.jsx';

/*
  Vitals rules
  Include head circ up to 3 years (inclusive)
  hgb in 6 month, 9 month, each from 2 year
  bp starting from 3 years (inclusive)
  display length as height from 3 years (inclusive)
  HIV rapid test for 12 month, 18 month
*/

type Props = {
  vitals: Object,
  type: number,
}

class ViewVitals extends React.Component<Props> {
  LOW_HEMOGLOBIN_THRESHOLD = 9;

  render() {
    const {
      weight, length, headCircumference, bp, bpSite, bpPosition,
      temperature, temperatureSite, hr, hrSite, rr, hgb, hgbIron, spo2, spo2Site,
      bpDMin, bpDMax, bpSMin, bpSMax, hrMin, hrMax, rrMin, rrMax, tempMin, tempMax,
      hivTest,
    } = this.props.vitals;

    let tempContent;
    if (temperature === undefined || temperature === null) {
      tempContent = '--';
    } else {
      const tempC = ((temperature - 32) * 5 / 9).toFixed(1);
      tempContent = `${temperature} °F (${tempC} °C)`;
      if (temperature > tempMax) {
        tempContent = <Error>{`${tempContent} (H)`}</Error>;
      } else if (temperature < tempMin) {
        tempContent = <Error>{`${tempContent} (L)`}</Error>;
      }
    }

    let hrContent;
    if (hr === undefined || hr === null) {
      hrContent = '--';
    } else if (hr > hrMax) {
      hrContent = <span><Error>{`${hr} bpm (H)`}</Error></span>;
    } else if (hr < hrMin) {
      hrContent = <span><Error>{`${hr} bpm (L)`}</Error></span>;
    } else {
      hrContent = `${hr} bpm`;
    }

    let rrContent;
    if (rr === undefined || rr === null) {
      rrContent = '--';
    } else if (rr > rrMax) {
      rrContent = <span><Error>{`${rr} breaths per min (H)`}</Error></span>;
    } else if (rr < rrMin) {
      rrContent = <span><Error>{`${rr} breaths per min (L)`}</Error></span>;
    } else {
      rrContent = `${rr} breaths per min`;
    }

    let bpContent;
    if (bp === undefined || bp === null || bp === '') {
      bpContent = '--';
    } else {
      let [bpS, bpD] = bp.split('/');
      if (bpS > bpSMax) {
        bpS = <Error>{`${bpS} (H)`}</Error>;
      } else if (bpS < bpSMin) {
        bpS = <Error>{`${bpS} (L)`}</Error>;
      }
      if (bpD > bpDMax) {
        bpD = <Error>{`${bpD} (H)`}</Error>;
      } else if (bpD < bpDMin) {
        bpD = <Error>{`${bpD} (L)`}</Error>;
      }
      bpContent = <span>{bpS}/{bpD}</span>;
    }

    let spo2Content;
    const SPO2_MIN_THRESHOLD = 95;
    if (spo2 === undefined || spo2 === null) {
      spo2Content = '--';
    } else if (spo2 < SPO2_MIN_THRESHOLD) {
      spo2Content = <span><Error>{`${spo2}% (H)`}</Error></span>;
    } else {
      spo2Content = `${spo2}%`;
    }

    let hgbContent;
    if (hgb === undefined || hgb === null) {
      hgbContent = '--';
    } else if (hgb < this.LOW_HEMOGLOBIN_THRESHOLD) {
      hgbContent = <Error>{`${hgb} g/dL (L)`}</Error>;
    } else {
      hgbContent = `${hgb} g/dL`;
    }

    return (
      <div>
        <PrimaryText>General</PrimaryText>
        <table>
          <tbody>
            <tr>
              <td><Label>Weight:</Label></td>
              <td>{weight || '--'} kg ({weight ? (weight * 2.2).toFixed(1) : '--'} lb)</td>
            </tr>
            <tr>
              <td><Label>{this.props.type > 7 ? 'Height:' : 'Length:'}</Label></td>
              <td>{length || '--'} cm ({length ? (length * 0.3937).toFixed(1) : '--'} in)</td>
            </tr>
            {this.props.type < 9
              && <tr>
                <td width='175'><Label>Head Circumference: </Label></td>
                <td>{headCircumference || '--'} cm ({headCircumference ? (headCircumference * 0.3937).toFixed(1) : '--'} in)</td>
              </tr>
            }
            <tr>
              <td><Label>HGB/HCT:</Label></td>
              <td>{hgbContent}</td>
            </tr>
            <tr>
              <td><Label>For Hb Recheck:</Label></td>
              <td>{hgbIron === false ? '✗' : hgbIron ? '✓' : '--'}</td>
            </tr>
            {(this.props.type === 5 || this.props.type === 6) // 12m and 18m WCCs only
              && <tr>
                <td><Label>Rapid HIV Test:</Label></td>
                <td>{hivTest || '--'}</td>
              </tr>
            }
          </tbody>
        </table>
        <br />
        <PrimaryText>Vitals</PrimaryText>
        <table>
          <tbody>
            <tr>
              <td><Label>Temperature:</Label></td>
              <td>{tempContent}</td>
            </tr>
            <tr>
              <td>Site:</td>
              <td>{temperatureSite || '--'}</td>
            </tr>
            <tr>
              <td><Label>Respiration Rate: </Label></td>
              <td>{rrContent}</td>
            </tr>
            <tr>
              <td><Label>Heart Rate:</Label></td>
              <td>{hrContent}</td>
            </tr>
            <tr>
              <td>Site:</td>
              <td>{hrSite || '--'}</td>
            </tr>
            <tr>
              <td><Label>BP:</Label></td>
              <td>{bpContent}</td>
            </tr>
            <tr>
              <td>Site:</td>
              <td>{bpSite || '--'}</td>
            </tr>
            <tr>
              <td>Position:</td>
              <td>{bpPosition || '--'}</td>
            </tr>
            <tr>
              <td><Label>SpO2:</Label></td>
              <td>{spo2Content}</td>
            </tr>
            <tr>
              <td>Site:</td>
              <td>{spo2Site || '--'}</td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }
}

export default ViewVitals;



// WEBPACK FOOTER //
// ./client/src/components/patientPages/wellChildCheck/view/ViewVitals.jsx